// How NinjaEval calls the customer's existing chat API: a preset, or a custom request template + reply/tool-call paths.
// Mirrors services/shared/endpoint_mapping.py.
import React from 'react';

export const PRESETS = {
  evalforge: { label: 'NinjaEval format', d: '{"message": …} → {"reply": …, "tool_calls": […]}',
    request_template: '{"message": "{{message}}", "system_prompt": "{{system_prompt}}", "model": "{{model}}", "version": "{{version}}"}', reply_path: '', tool_calls_path: 'tool_calls' },
  openai: { label: 'OpenAI-compatible chat', d: '{"messages": [...]} → choices[0].message.content (also vLLM, LiteLLM, Groq, Azure)',
    request_template: '{"model": "{{model}}", "messages": [{"role": "system", "content": "{{system_prompt}}"}, {"role": "user", "content": "{{message}}"}]}',
    reply_path: 'choices.0.message.content', tool_calls_path: 'choices.0.message.tool_calls' },
  custom: { label: 'Custom', d: 'Your own request body, and where the reply is in the response' },
};

export const effective = (c) => {
  const fmt = c?.format || 'evalforge';
  if (fmt !== 'custom') return { format: fmt, ...PRESETS[fmt] };
  return { format: 'custom', request_template: c.request_template ?? PRESETS.evalforge.request_template,
    reply_path: c.reply_path ?? '', tool_calls_path: c.tool_calls_path ?? '' };
};

export function EndpointFormat({ value, onChange, disabled }) {
  const c = effective(value);
  const set = (k, v) => onChange({ format: 'custom', request_template: c.request_template, reply_path: c.reply_path, tool_calls_path: c.tool_calls_path, [k]: v });
  let jsonErr = null;
  try { JSON.parse(c.request_template.replace(/\{\{\s*(message|system_prompt|model|version)\s*\}\}/g, 'x')); } catch (e) { jsonErr = 'Not valid JSON yet (placeholders go inside quotes)'; }
  return (
    <div className="ef-field" style={{ margin: 0 }}>
      <label>Request format</label>
      <div className="ef-fmt">{Object.entries(PRESETS).map(([k, p]) =>
        <button key={k} type="button" disabled={disabled} className={c.format === k ? 'on' : ''}
          onClick={() => onChange(k === 'custom' ? { format: 'custom', request_template: c.request_template, reply_path: c.reply_path, tool_calls_path: c.tool_calls_path } : { format: k })}>
          <b>{p.label}</b><span>{p.d}</span></button>)}</div>
      {c.format === 'custom' && <div className="ef-fmt-custom">
        <div className="ef-field"><label>Request body (JSON) <span className="fnt">placeholders: {'{{message}} {{system_prompt}} {{model}} {{version}}'}</span></label>
          <textarea className="ef-inp" style={{ minHeight: 84, fontSize: 12 }} disabled={disabled} value={c.request_template} onChange={(e) => set('request_template', e.target.value)} />
          {jsonErr && <span className="xs dn">{jsonErr}</span>}</div>
        <div className="ef-grid g2">
          <div className="ef-field"><label>Reply is at</label><input className="ef-inp mono" disabled={disabled} value={c.reply_path} onChange={(e) => set('reply_path', e.target.value.trim())} placeholder="e.g. output.text" /></div>
          <div className="ef-field"><label>Tool calls are at <span className="fnt">(optional)</span></label><input className="ef-inp mono" disabled={disabled} value={c.tool_calls_path} onChange={(e) => set('tool_calls_path', e.target.value.trim())} placeholder="e.g. output.tool_calls" /></div>
        </div>
        <div className="xs fnt">Tip: send a message with <b>Try it</b>, then click the field in the response that holds the reply.</div>
      </div>}
    </div>
  );
}

// Leaves of a JSON response, so the user can click the one that holds the reply (or the list of tool calls).
function leaves(node, path = [], out = []) {
  if (out.length > 60) return out;
  if (Array.isArray(node)) {
    if (node.length && node.every((x) => x && typeof x === 'object')) out.push({ path: path.join('.'), kind: 'list', preview: `${node.length} item${node.length > 1 ? 's' : ''}` });
    node.slice(0, 5).forEach((x, i) => leaves(x, [...path, String(i)], out));
  } else if (node && typeof node === 'object') {
    Object.entries(node).forEach(([k, v]) => leaves(v, [...path, k], out));
  } else if (typeof node === 'string' && node.trim()) {
    out.push({ path: path.join('.'), kind: 'text', preview: node.length > 90 ? node.slice(0, 90) + '…' : node });
  }
  return out;
}

export function ResponsePicker({ raw, current, onPick }) {
  const items = leaves(raw).filter((it) => !/^ninjaeval(_turns)?(\.|$)/.test(it.path));   // the ninjaeval block is read automatically
  return (
    <details className="ef-more" open={!current?.reply_path && !!onPick}>
      <summary>Raw response · click the field that holds the reply</summary>
      <div className="ef-picker">{items.map((it) =>
        <div key={it.path + it.kind} className={'row' + (current?.reply_path === it.path || current?.tool_calls_path === it.path ? ' on' : '')}>
          <span className="mono xs">{it.path || '(whole response)'}</span><span className="sm">{it.preview}</span>
          {onPick && (it.kind === 'text'
            ? <button type="button" className="ef-btn sm ghost" onClick={() => onPick('reply_path', it.path)}>Use as reply</button>
            : <button type="button" className="ef-btn sm ghost" onClick={() => onPick('tool_calls_path', it.path)}>Use as tool calls</button>)}
        </div>)}
        {!items.length && <div className="sm fnt">The response had no text fields.</div>}</div>
      <pre className="code" style={{ maxHeight: 220, overflow: 'auto' }}>{JSON.stringify(raw, null, 2)}</pre>
    </details>
  );
}
